import React, { useEffect, useState } from 'react';

const DEFINITION =
  'Tribe is a social platform that helps strangers meet and build real relationships through shared, in-person experiences.';

const STEPS = [
  {
    type: 'lesson',
    narration:
      `So, before we begin, let’s start with what Tribe is. ${DEFINITION}`,
  },
{
  type: 'question',
  prompt: 'What is Tribe?',
  choices: [
    {
      value: 'A',
      label:
        'A service for organizing activities with existing friends',
    },
    {
      value: 'B',
      label:
        'An app for discovering upcoming events around Los Angeles',
    },
    {
      value: 'C',
      label:
        'An invite-only social platform that helps strangers build real relationships through shared, in-person experiences',
    },
  ],
  correct: 'C',
  explanation:
    'Tribe brings strangers together through thoughtfully designed, hosted experiences that make forming real relationships easier.',
},
  {
    type: 'lesson',
    narration:
      'What makes Tribe different? First, every experience is led by a trained Tribe host who guides the group and makes sure everyone feels included. Second, every gathering is intentionally designed around a unique activity, giving people an easy, natural way to connect. And third, Tribe is invite-only, which allows us to thoughtfully curate the community and bring together people who are likely to get along and add to the experience.',
  },
];

const PART_TIMINGS = {
  0: [1700, 3800, 5300, 6800, 8300, 10300],
  2: [2500, 10500, 18300],
};

const DEFINITION_CAPTIONS = [
  {
    segments: [
      {
        text: 'So, before we begin',
      },
    ],
  },
  {
    segments: [
      {
        text: 'let’s start with what Tribe is',
      },
    ],
  },
  {
    segments: [
      {
        text: 'Tribe is a social platform',
      },
    ],
  },
  {
    segments: [
      {
        text: 'that helps strangers meet',
      },
    ],
  },
  {
    segments: [
      {
        text: 'and build',
      },
      {
        text: 'real relationships',
        handwritten: true,
      },
    ],
  },
  {
    segments: [
      {
        text: 'through',
      },
      {
        text: 'shared, in-person experiences',
        handwritten: true,
        long: true,
      },
    ],
  },
];

const DIFFERENCES = [
  {
    number: '01',
    title: 'Trained hosts',
    description:
      'They guide the group and make sure everyone feels included.',
  },
  {
    number: '02',
    title: 'Intentional activities',
    description:
      'They give people an easy, natural way to connect.',
  },
  {
    number: '03',
    title: 'Invite-only',
    description:
      'It allows Tribe to thoughtfully curate the community.',
  },
];

export default function WhatIsTribe({
  part,
  Question,
  selected,
  onSelect,
  audioStatus,
  playbackId,
}) {
  const step = STEPS[part];
  const [visualStage, setVisualStage] = useState(0);

  useEffect(() => {
    if (
      step?.type !== 'lesson' ||
      audioStatus !== 'Playing'
    ) {
      return undefined;
    }

    setVisualStage(0);

    const timers = (PART_TIMINGS[part] || []).map(
      (time, index) =>
        window.setTimeout(
          () => setVisualStage(index + 1),
          time
        )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [part, step?.type, audioStatus, playbackId]);

  useEffect(() => {
    if (audioStatus !== 'Complete') {
      return;
    }

    if (part === 0) {
      setVisualStage(DEFINITION_CAPTIONS.length);
    }

    if (part === 2) {
      setVisualStage(DIFFERENCES.length + 1);
    }
  }, [part, audioStatus]);

  if (step.type === 'question') {
    return (
      <Question
        {...step}
        selected={selected}
        onSelect={onSelect}
      />
    );
  }

  return (
    <section className="tribe-concept-slide">
      <style>{STYLES}</style>

      {part === 0 ? (
        <DefinitionSequence stage={visualStage} />
      ) : (
        <DifferenceField stage={visualStage} />
      )}
    </section>
  );
}

function DefinitionSequence({ stage }) {
  const finished =
    stage >= DEFINITION_CAPTIONS.length;

  const caption =
    DEFINITION_CAPTIONS[
      Math.min(
        stage,
        DEFINITION_CAPTIONS.length - 1
      )
    ];

  if (finished) {
    return (
      <div className="definition-review">
        <WordText
          as="h1"
          text={DEFINITION}
          wordDelay={72}
        />
      </div>
    );
  }

  return (
    <div
      className="definition-caption"
      key={stage}
    >
      <MixedText
        as="h1"
        segments={caption.segments}
        wordDelay={210}
      />
    </div>
  );
}

function DifferenceField({ stage }) {
  if (stage === 0) {
    return (
      <div className="tribe-concept-intro">
        <MixedText
          as="h1"
          segments={[
            {
              text: 'What makes Tribe',
            },
            {
              text: 'different?',
              handwritten: true,
            },
          ]}
          wordDelay={185}
        />
      </div>
    );
  }

  const reviewing =
    stage > DIFFERENCES.length;

  const currentIndex = Math.min(
    stage - 1,
    DIFFERENCES.length - 1
  );

  return (
    <div
      className={[
        'difference-field',
        reviewing ? 'is-reviewing' : '',
      ].join(' ')}
    >
      <h1 className="difference-field-title">
        <span>What makes Tribe</span>

        <span className="handwritten-static">
          different?
        </span>
      </h1>

      <div className="difference-field-items">
        {DIFFERENCES.map((difference, index) => {
          const revealed =
            reviewing || index <= currentIndex;

          const current =
            !reviewing && index === currentIndex;

          return (
            <div
              className={[
                'difference-field-item',
                revealed ? 'is-revealed' : '',
                current ? 'is-current' : '',
              ].join(' ')}
              key={difference.number}
            >
              <span className="difference-number">
                {difference.number}
              </span>

              {current ? (
                <WordText
                  as="h2"
                  text={difference.title}
                  wordDelay={140}
                />
              ) : (
                <h2>{difference.title}</h2>
              )}

              {current ? (
                <WordText
                  as="p"
                  text={difference.description}
                  wordDelay={75}
                  delay={420}
                />
              ) : (
                <p>{difference.description}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MixedText({
  as: Element,
  segments,
  wordDelay,
}) {
  let wordIndex = 0;

  const ariaText = segments
    .map((segment) => segment.text)
    .join(' ');

  return (
    <Element aria-label={ariaText}>
      {segments.map((segment, segmentIndex) => {
        const words = segment.text.split(' ');

        if (segment.handwritten) {
          const delay = wordIndex * wordDelay;

          wordIndex += words.length;

          return (
            <React.Fragment
              key={`${segment.text}-${segmentIndex}`}
            >
              {!segment.long && segmentIndex > 0
                ? ' '
                : ''}

              <span
                className={[
                  'tribe-handwritten-phrase',
                  segment.long ? 'is-long' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  '--tribe-word-delay': `${delay}ms`,
                }}
                aria-hidden="true"
              >
                {segment.text}
              </span>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment
            key={`${segment.text}-${segmentIndex}`}
          >
            {segmentIndex > 0 ? ' ' : ''}

            {words.map((word, localIndex) => {
              const delay = wordIndex * wordDelay;

              wordIndex += 1;

              return (
                <React.Fragment
                  key={`${word}-${localIndex}`}
                >
                  <span
                    className="tribe-concept-word"
                    style={{
                      '--tribe-word-delay': `${delay}ms`,
                    }}
                    aria-hidden="true"
                  >
                    {word}
                  </span>

                  {localIndex < words.length - 1
                    ? ' '
                    : ''}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </Element>
  );
}

function WordText({
  as: Element,
  text,
  wordDelay,
  delay = 0,
}) {
  const words = text.split(' ');

  return (
    <Element aria-label={text}>
      {words.map((word, index) => (
        <React.Fragment
          key={`${word}-${index}`}
        >
          <span
            className="tribe-concept-word"
            style={{
              '--tribe-word-delay':
                `${delay + index * wordDelay}ms`,
            }}
            aria-hidden="true"
          >
            {word}
          </span>

          {index < words.length - 1
            ? ' '
            : ''}
        </React.Fragment>
      ))}
    </Element>
  );
}

WhatIsTribe.title = 'What is Tribe?';
WhatIsTribe.steps = STEPS;

const STYLES = `
  .tribe-concept-slide {
    --lesson-font:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;

    --lesson-ink: #111111;
    --lesson-muted: #818181;
    --lesson-rule: #dedede;

    --lesson-hero-size:
      clamp(42px, 5.4vw, 70px);

    --lesson-hero-weight: 820;
    --lesson-hero-line: 1;
    --lesson-hero-tracking: -0.045em;

    --lesson-section-size:
      clamp(28px, 3vw, 39px);

    --lesson-section-weight: 800;

    --lesson-card-title-size:
      clamp(21px, 2.45vw, 31px);

    --lesson-card-title-weight: 780;

    --lesson-body-size:
      clamp(11px, 1.3vw, 15px);

    position: relative;
    width: 100%;
    min-height: 465px;
    overflow: hidden;
    box-sizing: border-box;

    color: var(--lesson-ink);
    background: #ffffff;

    font-family: var(--lesson-font);
  }

  /*
   * HANDWRITING
   */

  .tribe-handwritten-phrase,
  .handwritten-static {
    display: inline-block;

    margin-inline: 0.19em;
    padding-inline: 0.08em;

    font-family:
      'Ugly Handwriting',
      cursive;

    font-size: 1.24em;
    font-weight: 400;
    line-height: 0.9;
    letter-spacing: 0;

    white-space: nowrap;
  }

  .tribe-handwritten-phrase {
    opacity: 0;

    transform:
      translateY(0.28em)
      rotate(-1.6deg);

    animation:
      tribe-handwritten-in
      430ms
      cubic-bezier(.18,.84,.22,1)
      forwards;

    animation-delay:
      var(--tribe-word-delay);
  }

  /*
   * Longer handwritten ideas get their own line.
   * They stay prominent without running off screen.
   */

  .tribe-handwritten-phrase.is-long {
    display: block;

    width: fit-content;
    max-width: calc(100vw - 80px);

    margin:
      0.12em
      auto
      0;

    padding-inline: 0.12em;

    font-size: 1.14em;

    white-space: nowrap;
  }

  .handwritten-static {
    transform:
      translateY(0.02em)
      rotate(-1.6deg);
  }

  /*
   * STANDARD WORD REVEAL
   */

  .tribe-concept-word {
    display: inline-block;

    opacity: 0;

    transform:
      translateY(0.38em);

    animation:
      tribe-concept-word-in
      340ms
      cubic-bezier(.18,.84,.22,1)
      forwards;

    animation-delay:
      var(--tribe-word-delay);
  }

  /*
   * DEFINITION SEQUENCE
   */

  .definition-caption,
  .definition-review,
  .tribe-concept-intro {
    position: absolute;
    inset: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 32px;

    box-sizing: border-box;
  }

  .definition-caption h1,
  .tribe-concept-intro h1 {
    width:
      min(
        1080px,
        calc(100% - 48px)
      );

    margin: 0;

    font-size:
      var(--lesson-hero-size);

    font-weight:
      var(--lesson-hero-weight);

    line-height:
      var(--lesson-hero-line);

    letter-spacing:
      var(--lesson-hero-tracking);

    text-align: center;
  }

  /*
   * Final definition is clean.
   */

  .definition-review h1 {
    width: min(900px, 100%);
    margin: 0;

    font-size:
      clamp(30px, 3.7vw, 48px);

    font-weight: 760;
    line-height: 1.1;
    letter-spacing: -0.034em;

    text-align: left;
  }

  /*
   * DIFFERENCE SCENE
   */

  .difference-field {
    position: absolute;
    inset: 0;

    padding: 40px 48px;

    box-sizing: border-box;
  }

  .difference-field-title {
    display: flex;

    align-items: baseline;
    justify-content: center;

    flex-wrap: wrap;

    margin: 0;

    font-size:
      var(--lesson-section-size);

    font-weight:
      var(--lesson-section-weight);

    line-height: 1.04;
    letter-spacing: -0.038em;

    text-align: center;

    opacity: 0;

    transform:
      translateY(-10px);

    animation:
      difference-field-title-in
      520ms
      ease
      forwards;
  }

  .difference-field-title
  .handwritten-static {
    margin-left: 0.2em;
    margin-right: 0.08em;

    font-size: 1.28em;
  }

  .difference-field-items {
    position: absolute;

    top: 54%;
    left: 50%;

    display: grid;

    grid-template-columns:
      repeat(
        3,
        minmax(0, 1fr)
      );

    width:
      min(
        1000px,
        calc(100% - 72px)
      );

    transform:
      translate(-50%, -50%);
  }

  .difference-field-item {
    display: flex;
    flex-direction: column;
    justify-content: center;

    min-height: 195px;

    padding: 0 36px;

    box-sizing: border-box;

    color: #b5b5b5;

    border-left:
      1px solid
      var(--lesson-rule);

    opacity: 0;

    transform:
      translateY(8px);

    transition:
      color 600ms ease,
      opacity 600ms ease,
      transform
        750ms
        cubic-bezier(.18,.84,.22,1);
  }

  .difference-field-item:first-child {
    border-left: 0;
  }

  .difference-field-item.is-revealed {
    color: #929292;

    opacity: 0.42;

    transform:
      translateY(0);
  }

  .difference-field-item.is-current {
    color:
      var(--lesson-ink);

    opacity: 1;

    transform:
      translateY(-6px);
  }

  .difference-field.is-reviewing
  .difference-field-item {
    color:
      var(--lesson-ink);

    opacity: 1;

    transform:
      translateY(0);
  }

  .difference-number {
    color: currentColor;

    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0.08em;
  }

  .difference-field-item h2 {
    margin: 13px 0 0;

    font-size:
      var(
        --lesson-card-title-size
      );

    font-weight:
      var(
        --lesson-card-title-weight
      );

    line-height: 1.02;
    letter-spacing: -0.035em;

    transition:
      font-size
      650ms
      cubic-bezier(.18,.84,.22,1);
  }

  .difference-field-item p {
    max-width: 255px;

    margin: 15px 0 0;

    color: currentColor;

    font-size:
      var(--lesson-body-size);

    font-weight: 430;
    line-height: 1.48;
  }

  .difference-field-item:not(.is-current) p {
    opacity: 0.7;
  }

  .difference-field.is-reviewing
  .difference-field-item h2 {
    font-size:
      clamp(19px, 2.2vw, 27px);
  }

  /*
   * ANIMATION
   */

  @keyframes tribe-concept-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes tribe-handwritten-in {
    to {
      opacity: 1;

      transform:
        translateY(0)
        rotate(-1.6deg);
    }
  }

  @keyframes difference-field-title-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /*
   * MOBILE
   */

  @media (max-width: 720px) {
    .tribe-concept-slide {
      min-height: 520px;
    }

    .definition-caption,
    .definition-review,
    .tribe-concept-intro {
      padding: 24px;
    }

    .definition-caption h1,
    .tribe-concept-intro h1 {
      width: 100%;

      font-size:
        clamp(38px, 10.5vw, 50px);
    }

    .definition-review h1 {
      font-size: 32px;
    }

    .tribe-handwritten-phrase,
    .handwritten-static {
      margin-inline: 0.14em;
      padding-inline: 0.06em;

      font-size: 1.2em;
    }

    .tribe-handwritten-phrase.is-long {
      max-width:
        calc(100vw - 44px);

      margin-top: 0.14em;

      padding-inline: 0.06em;

      font-size: 1.08em;
    }

    .difference-field {
      padding: 30px 18px;
    }

    .difference-field-title {
      font-size: 27px;
    }

    .difference-field-title
    .handwritten-static {
      margin-left: 0.16em;

      font-size: 1.24em;
    }

    .difference-field-items {
      top: 57%;

      grid-template-columns: 1fr;

      width:
        calc(100% - 34px);
    }

    .difference-field-item {
      min-height: 105px;

      padding: 14px 0;

      border-top:
        1px solid
        var(--lesson-rule);

      border-left: 0;
    }

    .difference-field-item:first-child {
      border-top: 0;
    }

    .difference-field-item h2 {
      margin-top: 7px;

      font-size: 22px;
    }

    .difference-field-item p {
      margin-top: 7px;

      font-size: 11px;
    }
  }

  @media (max-width: 480px) {
    .definition-caption h1 {
      font-size: 38px;
    }

    .definition-review h1 {
      font-size: 29px;
    }

    .tribe-handwritten-phrase {
      font-size: 1.18em;
    }

    .tribe-handwritten-phrase.is-long {
      font-size: 1.03em;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .tribe-concept-slide,
    .tribe-concept-slide * {
      animation-duration:
        0.01ms !important;

      animation-delay:
        0ms !important;

      transition-duration:
        0.01ms !important;
    }
  }
`;